import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { PrivilegesGuard } from '@core/access-control/privileges.guard';
import { DictionariesGuard } from '@core/api/dictionaries.guard';
import { AuthGuard } from '@core/authentication/authentication.guard';
import { BreadcrumbsRouteData } from '@shared/breadcrumbs/breadcrumbs.typings';
import { IsEmittentGuard } from './core/authorization/is-emittent.guard';
import { IsInvestorGuard } from './core/authorization/is-investor.guard';
import { IsSuperUserGuard } from './core/authorization/is-super-user.guard';
import {
  administrationRoute,
  basketsRoute,
  clientAdvisorRoute,
  clientRoute,
  clientsRoute,
  contractsRoute,
  dataroomRoute,
  envelopesRoute,
  ddqInviteesRoute,
  fundDDQRunsRoute,
  investorRoute,
  issuingRoute,
  pendingAreaRoute,
  productRoute,
  profileRoute,
  reportSuspiciousActivityRoute,
  superUserRoute,
  userEntryRoute,
} from './shared/shared-routes';
import { OrganizationGuard } from '@core/organization/organization.guard';
import { retryDynamicImport } from './shared/chunk-retry';
import { PendingAreaContentResolver } from '@core/pending-area-content/pending-area-content.resolver';
import { PendingAreaGuard } from '@core/pending-area-content/pending-area.guard';
import { NoInvestmentMemorandumForIssuerGuard } from '@core/authorization/no-investment-memorandum-for-issuer.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: productRoute,
    pathMatch: 'full',
  },

  // NOTE: The `productRoute` uses children, due to guards are executed in parallel.
  // `DictionariesGuard` and `PrivilegesGuard` requires an authenticated user to not throw 403 error,
  // in this specific case firstly we check the `AuthGuard`, if it's ok, then we proceed futher.
  {
    path: productRoute,
    canActivate: [AuthGuard, OrganizationGuard, PendingAreaGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          retryDynamicImport(() => import('./feature/product/product.module').then((m) => m.ProductModule)),
        canActivate: [IsEmittentGuard, DictionariesGuard, PrivilegesGuard],
        data: {
          breadcrumb: 'INTL_NEW_PRODUCT',
        } as BreadcrumbsRouteData,
      },
    ],
  },
  {
    path: clientRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/client-user/client-user.module').then((m) => m.ClientUserModule)),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: administrationRoute,
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/administration.module').then((m) => m.AdministrationModule),
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_ADMIN_LABEL',
    } as BreadcrumbsRouteData,
  },

  {
    path: issuingRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/emittent/emittent.module').then((m) => m.EmittentModule)),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_EMISSIONS',
    } as BreadcrumbsRouteData,
  },

  {
    path: investorRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/investor/investor.module').then((m) => m.InvestorModule)),
    canActivate: [AuthGuard, OrganizationGuard, IsInvestorGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_EMISSIONS',
    } as BreadcrumbsRouteData,
  },
  {
    path: dataroomRoute,
    canActivate: [AuthGuard, OrganizationGuard, PendingAreaGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          retryDynamicImport(() =>
            import('./feature/user-dataroom/user-dataroom.module').then((m) => m.UserDataroomModule),
          ),
        canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard],
        data: {
          breadcrumb: 'INTL_NEW_PRODUCT',
        } as BreadcrumbsRouteData,
      },
    ],
  },
  {
    path: clientAdvisorRoute,
    canActivate: [AuthGuard, OrganizationGuard, PendingAreaGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          retryDynamicImport(() =>
            import('./feature/client-advisors/client-advisors.module').then((m) => m.ClientAdvisorsModule),
          ),
        canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard],
        data: {
          breadcrumb: 'INTL_NEW_PRODUCT',
        } as BreadcrumbsRouteData,
      },
    ],
  },
  {
    path: clientsRoute,
    canActivate: [AuthGuard, OrganizationGuard, PendingAreaGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          retryDynamicImport(() => import('./feature/clients/clients.module').then((m) => m.ClientsModule)),
        canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard],
        data: {
          breadcrumb: 'INTL_CLIENTS',
        } as BreadcrumbsRouteData,
      },
    ],
  },
  {
    path: basketsRoute,
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/investor-baskets/investor-baskets.module').then((m) => m.InvestorBasketsModule),
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_CLIENTS',
    } as BreadcrumbsRouteData,
  },
  {
    path: fundDDQRunsRoute,
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/fund-ddq-runs/fund-ddq-runs.module').then((m) => m.FundDDQRunsModule),
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_FUND_DDQ_RUNS',
    } as BreadcrumbsRouteData,
  },
  {
    path: ddqInviteesRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/invitees/invitees.module').then((m) => m.InviteesModule)),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_DDQ_INVITEES',
    } as BreadcrumbsRouteData,
  },
  {
    path: `${fundDDQRunsRoute}/${ddqInviteesRoute}`,
    redirectTo: `/${ddqInviteesRoute}`,
    pathMatch: 'full',
  },
  {
    path: envelopesRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/envelope/envelope.module').then((m) => m.EnvelopeModule)),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: contractsRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/contracts/contracts.module').then((m) => m.ContractsModule)),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      breadcrumb: 'INTL_CONTRACTS',
    } as BreadcrumbsRouteData,
  },
  {
    path: profileRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/user-profile/user-profile.module').then((m) => m.UserProfileModule)),
    canActivate: [AuthGuard, DictionariesGuard], // no `PrivilegesGuard` due to universal type of this page (user and superuser)
    data: {
      breadcrumb: 'INTL_USER_PROFILE',
    } as BreadcrumbsRouteData,
  },
  {
    path: superUserRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/super-user/super-user.module').then((m) => m.SuperUserModule)),
    canActivate: [AuthGuard, IsSuperUserGuard, DictionariesGuard],
  },
  {
    path: userEntryRoute,
    loadChildren: () => retryDynamicImport(() => import('./feature/user/user.module').then((m) => m.UserModule)),
  },
  {
    path: reportSuspiciousActivityRoute,
    loadChildren: () => retryDynamicImport(() => import('./feature/user/report-suspicious-activity/report-suspicious-activity.module').then((m) => m.ReportSuspiciousActivityModule)),
  },
  {
    path: pendingAreaRoute,
    loadChildren: () =>
      retryDynamicImport(() => import('./feature/pending-area/pending-area.module').then((m) => m.PendingAreaModule)),
    canActivate: [AuthGuard],
    resolve: {
      pendingAreaContent: PendingAreaContentResolver,
    },
    data: {
      breadcrumb: 'INTL_PENDING_AREA',
    } as BreadcrumbsRouteData,
  },
  {
    path: 'due-diligence-listing/:id',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/product/due-diligence/due-diligence-listing/due-diligence-listing-module').then(
          (m) => m.DueDiligenceListingModule
        )
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'investment-memorandum-details/:productId',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/product/investment-memorandum/dataview-investment-memo/dataview-investment-memo-module').then(
          (m) => m.DataviewInvestmentMemoModule
        )
      ),
    canActivate: [
      AuthGuard,
      OrganizationGuard,
      DictionariesGuard,
      PrivilegesGuard,
      PendingAreaGuard,
      NoInvestmentMemorandumForIssuerGuard,
    ],
    data: {
      allowWithoutOrganization: true,
    },
  },
  {
    path: 'question-library',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/question-library/question-library-module').then(
          (m) => m.QuestionLibraryModule
        )
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      allowWithoutOrganization: true,
    },
  },
  {
    path: ':clientId/investment-memorandum-details',
    loadChildren: () =>
      retryDynamicImport(() =>
        import(
          './feature/administration/configuration-layout/investment-memorandum-details/investment-memorandum-details/investment-memorandum-details-routing.module'
        ).then(m => m.InvestmentMemorandumDetailsRoutingModule)
      ),
    canActivate: [
      AuthGuard,
      OrganizationGuard,
      DictionariesGuard,
      PrivilegesGuard,
      PendingAreaGuard
    ],
    data: {
      allowWithoutOrganization: true
    }
  },
  {
    path: 'investment-memorandum-details',
    loadChildren: () =>
      retryDynamicImport(() =>
        import(
          './feature/administration/configuration-layout/investment-memorandum-details/investment-memorandum-details/investment-memorandum-details-routing.module'
        ).then(m => m.InvestmentMemorandumDetailsRoutingModule)
      ),
    canActivate: [
      AuthGuard,
      OrganizationGuard,
      DictionariesGuard,
      PrivilegesGuard,
      PendingAreaGuard
    ],
    data: {
      allowWithoutOrganization: true
    }
  },
  {
    path: 'accuracy-ai-dashboard',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/super-user/accuracy-ai-dashboard/accuracy-ai-dashboard.module').then(
          (m) => m.AccuracyAiDashboardModule
        )
      ),
    canActivate: [AuthGuard, OrganizationGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
    data: {
      allowWithoutOrganization: true,
    },
  },
  {
    path: 'template-dd-list',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/template-dd-list/template-dd-list-module').then(
          (m) => m.TemplateDdListModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'create-template/:id',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template/create-template-listing/create-template-listing-module').then(
          (m) => m.CreateTemplateListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'create-template',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template/create-template-listing/create-template-listing-module').then(
          (m) => m.CreateTemplateListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'template-versions',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template-im/template-versions-listing/template-versions-listing-module').then(
          (m) => m.TemplateVersionsListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'create-template-im/:id',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template-im/create-template-listing/create-template-listing-module').then(
          (m) => m.CreateTemplateListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'create-template-im',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template-im/create-template-listing/create-template-listing-module').then(
          (m) => m.CreateTemplateListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: 'template-versions',
    loadChildren: () =>
      retryDynamicImport(() =>
        import('./feature/administration/configuration-layout/create-template-im/template-versions-listing/template-versions-listing-module').then(
          (m) => m.TemplateVersionsListingModule
        )
      ),
    canActivate: [AuthGuard, DictionariesGuard, PrivilegesGuard, PendingAreaGuard],
  },
  {
    path: '**',
    redirectTo: `${userEntryRoute}/404`,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // decouple components from routing tree so we don't have to do stuff like `activatedRoute.parent.parent.snapshot.data`
      enableTracing: false,
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
